import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * The launch failure vocabulary. **This is UX, not just logs** (§5.4): there is one
 * mint-only endpoint and no eligibility query, so an un-provisioned prescriber discovers
 * it by clicking — the blocked-provider state is a primary state, and each code below has
 * fixed modal copy in §10.4.
 *
 * They travel as the `code` of an `ORPCError`, so the browser switches on
 * `error.code` rather than parsing a message.
 */
export const DOSESPOT_LAUNCH_ERRORS = {
  /** The caller's own access policy hides the patient — this *is* the partition (§5.2). */
  PATIENT_NOT_FOUND: 'PATIENT_NOT_FOUND',
  /** No DoseSpot clinician mapping on the caller. Fail-closed, and the entire gate (§5.1). */
  PRESCRIBER_NOT_MAPPED: 'PRESCRIBER_NOT_MAPPED',
  /** No clinic for this organization. Absent config is the feature's off switch (§14). */
  NOT_CONFIGURED: 'NOT_CONFIGURED',
  /** Presence pre-flight refusal, instant and before any DoseSpot call (§7.4). */
  PATIENT_INCOMPLETE: 'PATIENT_INCOMPLETE',
  /** The prescriber's token would not mint: locked, inactive, or outside the clinic (§6.3). */
  PRESCRIBER_ACCOUNT: 'PRESCRIBER_ACCOUNT',
  /** DoseSpot rejected the demographics; `data.fields` carries the translated map (§7.4). */
  PATIENT_REJECTED: 'PATIENT_REJECTED',
  /** Nothing was learned — a DoseSpot outage is not a data problem (§2.6). */
  DOSESPOT_UNAVAILABLE: 'DOSESPOT_UNAVAILABLE',
} as const

export type DoseSpotLaunchErrorCode =
  (typeof DOSESPOT_LAUNCH_ERRORS)[keyof typeof DOSESPOT_LAUNCH_ERRORS]

/**
 * Mints a single-use SSO URL for the prescribing iframe (§9.1). **One endpoint, mint
 * only** — the control renders for every non-manager and the click either returns a URL
 * or one of `DOSESPOT_LAUNCH_ERRORS`.
 *
 * `POST`, not `GET`: the response carries a live single-use credential that must never be
 * cacheable, bookmarkable, or resurrectable by the back button (§10.5).
 */
export const doseSpotLaunch = oc
  .route({ method: 'POST', path: '/v2/dosespot/launch' })
  .input(
    z.object({
      patientId: z.string().min(1),
      /** Round-trips onto the prescription record. Present in-call, absent from the chart. */
      encounterId: z.string().min(1).optional(),
    }),
  )
  .output(
    z.object({
      url: z.string(),
      /**
       * The allergy mirror fails **open** (§8.5): a partial or total failure still returns
       * a URL, and the modal shows an amber banner naming the un-synced allergens. Those
       * names are the chart's own data shown to the clinician who owns it — which is why
       * naming them here is fine while logging them is not (§13.3).
       */
      allergyWarning: z.object({ failed: z.array(z.string()) }).optional(),
    }),
  )

/**
 * The notification-bell launch: an SSO carrying `RefillsErrors=1` and **no `PatientId`**,
 * which lands the prescriber on DoseSpot's own notification summary (§12.5). Deep-linking
 * there is a **DoseSpot certification requirement**, not a convenience.
 *
 * Patient-less by construction, and that is the whole difference from `doseSpotLaunch`: with
 * no patient there is no chart read, no presence pre-flight, no patient `PUT` and no allergy
 * mirror — §9.1 steps 2, 3, 5 and 8 only. So `PATIENT_NOT_FOUND`, `PATIENT_INCOMPLETE` and
 * `PATIENT_REJECTED` are unreachable here; the other four codes are all live, and the bell's
 * modal renders them with the same copy the Prescribe modal does.
 *
 * `POST` for the same reason as `doseSpotLaunch`: the response carries a live single-use
 * credential that must never be cacheable or resurrectable by the back button (§10.5).
 */
export const doseSpotLaunchNotifications = oc
  .route({ method: 'POST', path: '/v2/dosespot/notifications/launch' })
  .input(z.object({}).optional())
  .output(z.object({ url: z.string() }))
